import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
  Select,
  Spinner,
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Icon,
  type SelectProps,
} from '@amzn/awsui-components-react';
import { useTaskData, fetchExecutions, invalidateExecCache, refreshExecutions, type ExecutionItemView } from './useTaskData';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useAgent } from '../../context/AgentContext';
import './TaskPage.css';

// ── Filter options ──────────────────────────────────────────────────────────

// Labels are keys, translated at render so they follow a locale change.
const TIME_RANGE_OPTIONS = [
  { value: '7', labelKey: 'history.last7' },
  { value: '30', labelKey: 'history.last30' },
  { value: '90', labelKey: 'history.last90' },
] as const;

const STATUS_OPTIONS = [
  { value: 'ALL', labelKey: 'history.statusAll' },
  { value: 'SUCCESS', labelKey: 'history.statusSuccess' },
  { value: 'FAILED', labelKey: 'history.statusFailed' },
  { value: 'RUNNING', labelKey: 'history.statusRunning' },
] as const;

// ── Types ───────────────────────────────────────────────────────────────────

interface DayGroup {
  date: string;
  dateKey: string;
  items: ExecutionItemView[];
  errorCount: number;
  totalCount: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDayHeader(dateStr: string, dateLocale: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const weekday = d.toLocaleDateString(dateLocale, { weekday: 'long' });
  const formatted = d.toLocaleDateString(dateLocale, { month: '2-digit', day: '2-digit', year: 'numeric' });
  return `${weekday}, ${formatted}`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.floor(ms / 1000);
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${remainSecs}s`;
}

function getDateKey(isoStr: string): string {
  const d = new Date(isoStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function ExecutionHistoryTab() {
  const { t } = useTranslation('tasks');
  const data = useTaskData();
  const { effectiveUid } = useImpersonation();
  const { currentAgent } = useAgent();

  const [timeRangeDays, setTimeRangeDays] = useState('90');
  const [statusValue, setStatusValue] = useState('ALL');
  const [contentTypeValue, setContentTypeValue] = useState('ALL');

  // Trigger execution fetch — single API call via ListExecutions.
  // fetchExecutions internally deduplicates — if the cache key matches it's a no-op.
  useEffect(() => {
    if (effectiveUid && data.tasksLoaded) {
      fetchExecutions(effectiveUid, parseInt(timeRangeDays, 10), statusValue, currentAgent);
    }
  }, [effectiveUid, data.tasksLoaded, timeRangeDays, statusValue, currentAgent]);

  const handleTimeRangeChange = (value: string) => {
    setTimeRangeDays(value);
    setContentTypeValue('ALL');
    invalidateExecCache();
  };

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    setContentTypeValue('ALL');
    invalidateExecCache();
  };

  const filteredExecutions = useMemo(() => {
    if (contentTypeValue === 'ALL') return data.executions;
    return data.executions.filter((e) => e.record.contentType === contentTypeValue);
  }, [data.executions, contentTypeValue]);

  const contentTypeOptions = useMemo<SelectProps.Option[]>(() => {
    const opts: SelectProps.Option[] = [{ value: 'ALL', label: t('history.contentTypeAll') }];
    data.execContentTypes.forEach((t) => opts.push({ value: t, label: t }));
    return opts;
  }, [data.execContentTypes, t]);

  const dayGroups = useMemo<DayGroup[]>(() => {
    const map = new Map<string, ExecutionItemView[]>();
    for (const item of filteredExecutions) {
      const key = getDateKey(item.record.executedAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateKey, items]) => ({
        date: formatDayHeader(dateKey, t('dateLocale')),
        dateKey,
        items: items.sort((a, b) => b.record.executedAt.localeCompare(a.record.executedAt)),
        errorCount: items.filter((i) => i.record.status === 'FAILED').length,
        totalCount: items.length,
      }));
  }, [filteredExecutions, t]);

  const timeRangeOptions: SelectProps.Option[] = TIME_RANGE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));
  const statusOptions: SelectProps.Option[] = STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));
  const timeRangeOption = timeRangeOptions.find((o) => o.value === timeRangeDays) ?? timeRangeOptions[2];
  const statusOption = statusOptions.find((o) => o.value === statusValue) ?? statusOptions[0];
  const contentTypeOption = contentTypeOptions.find((o) => o.value === contentTypeValue) ?? contentTypeOptions[0];

  if (data.tasksLoading && !data.tasksLoaded) {
    return <Box padding="l" textAlign="center"><Spinner size="large" /></Box>;
  }

  if (data.execError) {
    return (
      <Box padding="l" textAlign="center" color="inherit">
        <StatusIndicator type="error">{data.execError}</StatusIndicator>
      </Box>
    );
  }

  const handleRefresh = () => {
    if (effectiveUid) {
      refreshExecutions(effectiveUid, parseInt(timeRangeDays, 10), statusValue, currentAgent);
    }
  };

  return (
    <div className="exec-history">
      <div className="exec-history__filters">
        <Select
          selectedOption={timeRangeOption}
          onChange={({ detail }: { detail: { selectedOption: SelectProps.Option } }) => handleTimeRangeChange(detail.selectedOption.value ?? '90')}
          options={timeRangeOptions}
        />
        <Select
          selectedOption={statusOption}
          onChange={({ detail }: { detail: { selectedOption: SelectProps.Option } }) => handleStatusChange(detail.selectedOption.value ?? 'ALL')}
          options={statusOptions}
        />
        <Select
          selectedOption={contentTypeOption}
          onChange={({ detail }: { detail: { selectedOption: SelectProps.Option } }) => setContentTypeValue(detail.selectedOption.value ?? 'ALL')}
          options={contentTypeOptions}
        />
      </div>

      <Container
        header={
          <Header
            variant="h2"
            counter={`(${filteredExecutions.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="refresh"
                  loading={data.execLoading}
                  onClick={handleRefresh}
                />
              </SpaceBetween>
            }
          >
            {t('history.title')}
          </Header>
        }
      >
        {data.execLoading ? (
          <Box padding="l" textAlign="center"><Spinner size="large" /></Box>
        ) : dayGroups.length === 0 ? (
          <Box textAlign="center" color="inherit" padding="l">
            <b>{t('history.emptyTitle')}</b>
            <Box variant="p" color="inherit">{t('history.emptyBody')}</Box>
          </Box>
        ) : (
          <div className="exec-timeline">
            {dayGroups.map((group) => (
              <DayGroupSection key={group.dateKey} group={group} />
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}

// ── Day Group with timeline arrow ───────────────────────────────────────────

function DayGroupSection({ group }: { group: DayGroup }) {
  const { t } = useTranslation('tasks');
  const [expanded, setExpanded] = useState(true);

  // Mirrors today's `n > 1` rule for Item / Items.
  const itemCount = t(group.totalCount > 1 ? 'history.itemsMany' : 'history.itemsOne', { n: group.totalCount });
  const badgeText = group.errorCount > 0
    ? `${itemCount} ${t('history.errorsDetected', { n: group.errorCount })}`
    : itemCount;

  const hasError = group.errorCount > 0;

  return (
    <div className="exec-day-group">
      <div
        className="exec-day-group__header"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
      >
        <span className={`exec-day-group__arrow ${expanded ? 'exec-day-group__arrow--expanded' : ''}`}>
          ▶
        </span>
        <span className={`exec-day-group__badge ${hasError ? 'exec-day-group__badge--error' : 'exec-day-group__badge--ok'}`}>
          {group.date}, {badgeText}
        </span>
      </div>
      {expanded && (
        <div className="exec-day-group__items">
          {group.items.map((item, idx) => (
            <ExecutionItem key={`${item.record.executedAt}-${idx}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Execution Item ──────────────────────────────────────────────────────────

function ExecutionItem({ item }: { item: ExecutionItemView }) {
  const { t } = useTranslation('tasks');
  const { record } = item;
  const time = new Date(record.executedAt).toLocaleTimeString(t('dateLocale'), {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div className="exec-item" data-task-id={record.taskId}>
      <span className="exec-item__tooltip">{t('history.taskId', { id: record.taskId })}</span>
      <div className="exec-item__icon">
        <Icon
          name={record.status === 'FAILED' ? 'status-negative' : 'status-positive'}
          variant={record.status === 'FAILED' ? 'error' : 'success'}
        />
      </div>
      <div className="exec-item__body">
        <div className="exec-item__content">
          {record.content || item.taskName}
        </div>
        <div className="exec-item__meta">
          <span className="exec-item__meta-cell">{t('history.time', { time })}</span>
          <span className="exec-item__meta-cell">
            Status:{' '}
            <StatusIndicator
              type={
                record.status === 'SUCCESS' ? 'success'
                  : record.status === 'FAILED' ? 'error'
                    : 'in-progress'
              }
            >
              {record.status === 'SUCCESS' ? t('history.itemSuccess') : record.status === 'FAILED' ? t('history.itemError') : t('history.itemRunning')}
            </StatusIndicator>
          </span>
          <span className="exec-item__meta-cell">{t('history.contentType', { type: record.contentType || '-' })}</span>
          <span className="exec-item__meta-cell">{t('history.timeUsed', { duration: record.durationMs ? formatDuration(record.durationMs) : '-' })}</span>
        </div>
      </div>
    </div>
  );
}
